import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale != null) {
        return {
          id: sale.id,
          fullName: `${sale.first_name} ${sale.last_name}`,
          avatar: sale.avatar?.src,
          role: sale.administrator ? "admin" : "team",
        };
      }

      // Client portal users are not in sales: they get a client identity.
      const portalUser = await getPortalUser();
      if (portalUser == null) {
        throw new Error();
      }
      return {
        id: `portal-${portalUser.id}`,
        fullName:
          [portalUser.first_name, portalUser.last_name]
            .filter(Boolean)
            .join(" ") || portalUser.email,
        role: "client",
        companyId: portalUser.company_id,
        companyName: portalUser.company?.name ?? "",
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";
const CURRENT_PORTAL_USER_CACHE_KEY = "RaStore.auth.current_portal_user";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

const getSale = async () => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    return JSON.parse(cachedValue);
  }

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select("id, first_name, last_name, avatar, administrator")
    .match({ user_id: dataSession?.session?.user.id })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
  return dataSale;
};

export type PortalUser = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  company_id: number;
  company: { name: string } | null;
};

// The portal access is re-checked once per page load: if the client company
// was deleted or the access disabled, the cached copy must not keep the
// portal open.
let portalUserCheckedThisLoad = false;

const getPortalUser = async (): Promise<PortalUser | undefined> => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_PORTAL_USER_CACHE_KEY);
  if (cachedValue != null && portalUserCheckedThisLoad) {
    return JSON.parse(cachedValue);
  }
  portalUserCheckedThisLoad = true;
  storage?.removeItem(CURRENT_PORTAL_USER_CACHE_KEY);

  const { data: dataSession } = await getSupabaseClient().auth.getSession();
  const userId = dataSession?.session?.user.id;
  if (userId == null) {
    return undefined;
  }

  const { data, error } = await getSupabaseClient()
    .from("portal_users")
    .select(
      "id, first_name, last_name, email, company_id, company:companies(name)",
    )
    .match({ user_id: userId, disabled: false })
    .single();
  if (data == null || error) {
    return undefined;
  }

  storage?.setItem(CURRENT_PORTAL_USER_CACHE_KEY, JSON.stringify(data));
  return data as unknown as PortalUser;
};

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
  storage?.removeItem(CURRENT_PORTAL_USER_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // Users are opening a shared access link, nothing to do
      if (window.location.hash.startsWith("#/access")) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        await getSupabaseClient().auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      await baseAuthProvider.checkAuth(params);

      // A signed-in account that is neither agency staff nor an active
      // client (e.g. its client company was deleted) gets signed out.
      if ((await getSale()) == null && (await getPortalUser()) == null) {
        clearCache();
        await getSupabaseClient().auth.signOut();
        throw { redirectTo: "/login", message: "crm.auth.no_access" };
      }
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (!isInitialized) return false;

      // Get the current user
      const sale = await getSale();
      if (sale != null) {
        // Compute access rights from the sale role
        const role = sale.administrator ? "admin" : "user";
        return canAccess(role, params);
      }

      const portalUser = await getPortalUser();
      if (portalUser == null) return false;
      return canAccess("client", params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
